/**
 * @architecture-header 1
 * @layer routes
 * @domain workspace-shell
 * @responsibility Reproduce the global keyboard map (palette, G-sequences, review stepping, zoom).
 * @state-owner navigation store; module UI stores
 * @allowed-imports React; workspace store; activities module public API; shared components and hooks; role provider
 * @forbidden in-product role switching shortcut; direct API client
 * @public-api WorkspaceShell
 * @side-effects window keyboard listener
 * @tests workspace Playwright flows in frontend/apps/web/e2e/workspace.spec.ts and
 *   frontend/apps/web/e2e/act-f13.spec.ts
 */

import { useEffect, useRef } from 'react';
import { useActiveRole } from '../../app/providers/ActiveRoleProvider';
import { useCanStepReview, usePaperZoom, useReviewStepper } from '../../modules/activities';
import { useOverlay } from '../../shared/components/OverlayHost';
import type { WorkspaceSection } from '../../shared/lib/model';
import { useWorkspaceUiStore } from '../../shared/model/workspace-store';
import { ShortcutsDialog } from './ShortcutsDialog';

function isTyping(target: EventTarget | null): boolean {
  const element = target as HTMLElement | null;
  if (!element) return false;
  return element.tagName === 'INPUT' || element.tagName === 'TEXTAREA' || element.isContentEditable;
}

/** The  `⌘⇧R` role toggle is intentionally not bound: the role is fixed at first login. */
export function ShellKeyboard({
  onCloseSearch,
  onNavigateSection,
  onOpenPalette,
}: {
  onCloseSearch(): void;
  onNavigateSection(section: WorkspaceSection): void;
  onOpenPalette(): void;
}) {
  const role = useActiveRole();
  const teacher = role === 'teacher';
  const { openModal, closeModal, closePopover, isModalOpen, isPopoverOpen } = useOverlay();
  const stepReview = useReviewStepper();
  const canStepReview = useCanStepReview();
  const changeZoom = usePaperZoom();
  const sequenceRef = useRef(0);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      const store = useWorkspaceUiStore.getState();

      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        onOpenPalette();
        return;
      }

      if (!isTyping(event.target) && !event.metaKey && !event.ctrlKey && !event.altKey) {
        const key = event.key.toLowerCase();
        if (Date.now() - sequenceRef.current < 900) {
          const target = ({ a: 'act', c: 'room', l: 'live', r: 'rep' } as const)[
            key as 'a' | 'c' | 'l' | 'r'
          ];
          sequenceRef.current = 0;
          if (target && (teacher || target !== 'live')) {
            event.preventDefault();
            onNavigateSection(target);
            return;
          }
        }
        if (key === 'g') {
          sequenceRef.current = Date.now();
          return;
        }
        if (event.key === '?') {
          event.preventDefault();
          openModal(<ShortcutsDialog />);
          return;
        }
        if ((key === 'n' || key === 'p') && teacher && canStepReview) {
          event.preventDefault();
          stepReview(key === 'n' ? 1 : -1);
          return;
        }
      }

      if (event.key === 'Escape') {
        onCloseSearch();
        if (isPopoverOpen) {
          closePopover();
          return;
        }
        if (isModalOpen) {
          closeModal();
          return;
        }
        if (store.open) store.closeItem();
        return;
      }

      if ((event.metaKey || event.ctrlKey) && event.key === '\\') {
        event.preventDefault();
        store.toggleList();
      }
      if (
        (event.metaKey || event.ctrlKey) &&
        (event.key === '=' || event.key === '+') &&
        store.section === 'act' &&
        teacher
      ) {
        event.preventDefault();
        changeZoom(10);
      }
      if (
        (event.metaKey || event.ctrlKey) &&
        event.key === '-' &&
        store.section === 'act' &&
        teacher
      ) {
        event.preventDefault();
        changeZoom(-10);
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [
    canStepReview,
    closeModal,
    closePopover,
    isModalOpen,
    isPopoverOpen,
    onCloseSearch,
    onNavigateSection,
    onOpenPalette,
    changeZoom,
    openModal,
    stepReview,
    teacher,
  ]);

  return null;
}
